import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CheckCircle, Copy, Check, Building2, MapPin, Mail, Hash } from "lucide-react";
import { useCompanyLogo } from "@/hooks/use-company-logo";

const onboardingSchema = z.object({
  name: z.string().min(1, "Företagsnamn krävs"),
  address: z.string().optional(),
  email: z.string().min(1, "E-postadress krävs").email("Ogiltig e-postadress"),
  orgNumber: z.string().min(1, "Organisationsnummer krävs"),
});

type OnboardingFormData = z.infer<typeof onboardingSchema>;

interface Credentials {
  username: string;
  password: string;
}

export default function ClientOnboarding() {
  const logoUrl = useCompanyLogo();
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<OnboardingFormData>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      name: "",
      address: "",
      email: "",
      orgNumber: "",
    },
  });

  const copyToClipboard = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // ignore
    }
  };

  const onSubmit = async (data: OnboardingFormData) => {
    setServerError(null);
    try {
      const response = await fetch("/api/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      const body = await response.json();
      if (!response.ok) {
        if (body.field === "email") {
          form.setError("email", { message: body.message });
        } else if (body.field === "orgNumber") {
          form.setError("orgNumber", { message: body.message });
        } else if (body.field === "name") {
          form.setError("name", { message: body.message });
        } else {
          setServerError(body.message || "Registrering misslyckades. Försök igen.");
        }
        return;
      }
      setCredentials({ username: body.username, password: body.password });
    } catch {
      setServerError("Registrering misslyckades. Kontrollera din anslutning och försök igen.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src={logoUrl}
              alt="Rätt Order"
              className="h-16 w-auto max-w-[200px] object-contain"
              data-testid="img-onboarding-logo"
            />
          </div>
          <CardTitle className="text-2xl">Registrera ditt företag</CardTitle>
          <CardDescription>
            Fyll i uppgifterna nedan för att skapa ett konto och börja beställa.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {credentials ? (
            <div className="space-y-5" data-testid="section-credentials-result">
              <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-300">Registrering klar!</p>
                  <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                    Spara dina inloggningsuppgifter nedan. De visas bara en gång.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Användarnamn</p>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={credentials.username}
                      className="font-mono bg-muted"
                      data-testid="text-onboarding-username"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(credentials.username, "username")}
                      data-testid="button-copy-username"
                    >
                      {copiedField === "username" ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Lösenord</p>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={credentials.password}
                      className="font-mono bg-muted"
                      data-testid="text-onboarding-password"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(credentials.password, "password")}
                      data-testid="button-copy-password"
                    >
                      {copiedField === "password" ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Gå till inloggningssidan och använd uppgifterna ovan för att logga in.
              </p>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Företagsnamn</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            placeholder="Ditt företagsnamn"
                            className="pl-9"
                            data-testid="input-onboarding-name"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Adress <span className="text-muted-foreground font-normal">(valfri)</span></FormLabel>
                      <FormControl>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            placeholder="Gatuadress, postnummer, stad"
                            className="pl-9"
                            data-testid="input-onboarding-address"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-postadress</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            type="email"
                            placeholder="din@epost.se"
                            className="pl-9"
                            data-testid="input-onboarding-email"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="orgNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organisationsnummer</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Hash className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            placeholder="556000-0000"
                            className="pl-9"
                            data-testid="input-onboarding-orgnumber"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {serverError && (
                  <p className="text-sm text-destructive" data-testid="text-onboarding-error">
                    {serverError}
                  </p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={form.formState.isSubmitting}
                  data-testid="button-onboarding-submit"
                >
                  {form.formState.isSubmitting ? "Registrerar..." : "Skapa konto"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
